import {
  AgentType,
  GEMINI_VOICE_GENDERS,
  GeminiVoices,
  Gender,
  Role,
} from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@utima/ui';
import { Input, type TypedFormState } from '@utima/ui-informed';
import { useFieldState, useFormApi } from 'informed';
import { Play, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { ListInput } from '@/components/listInput/ListInput';
import { useEnumTranslations } from '@/hooks';
import { useAccess } from '@/hooks/useAccess';

import { getVoicesForGender, voiceDescriptions } from '../voiceOptions';

/**
 * Plays a short Czech sample of whichever voice is currently selected.
 * Czech samples are generated with `scripts/generate-voice-samples.mjs` and committed as
 * static files under `public/voice-samples/`.
 */
function VoicePreviewButton() {
  const { t } = useLingui();
  const voiceField = useFieldState('voiceConfig.gemini.voice');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const audio = new Audio();
    audio.addEventListener('ended', () => setIsPlaying(false));
    audio.addEventListener('pause', () => setIsPlaying(false));
    audio.addEventListener('error', () => {
      setIsPlaying(false);
      setFailed(true);
    });
    audioRef.current = audio;

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    audioRef.current?.pause();
    setFailed(false);
  }, [voiceField.value]);

  const handleClick = async () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (isPlaying) {
      audio.pause();

      return;
    }

    const voice =
      typeof voiceField.value === 'string'
        ? voiceField.value
        : GeminiVoices.Puck;
    audio.src = `/voice-samples/${voice.toLowerCase()}.wav`;
    setFailed(false);
    try {
      await audio.play();
      setIsPlaying(true);
    } catch {
      setFailed(true);
      setIsPlaying(false);
    }
  };

  return (
    <Button
      type='button'
      variant='secondary'
      size='sm'
      onClick={handleClick}
      className='mt-2'
    >
      {isPlaying ? <Square className='size-4' /> : <Play className='size-4' />}
      {failed
        ? t`Preview unavailable. Try again`
        : isPlaying
          ? t`Stop preview`
          : t`Preview voice`}
    </Button>
  );
}

export function VoiceConfigFields() {
  const { hasAccess } = useAccess();
  const { t, i18n } = useLingui();
  const gender = useFieldState('agentConfig.gender').value;
  const voice = useFieldState('voiceConfig.gemini.voice').value;
  const formApi = useFormApi();
  const voices = getVoicesForGender(gender);

  useEffect(() => {
    if (
      typeof voice === 'string' &&
      !getVoicesForGender(gender).includes(voice as GeminiVoices)
    ) {
      formApi.setValue(
        'voiceConfig.gemini.voice',
        gender === Gender.Female ? GeminiVoices.Kore : GeminiVoices.Puck,
      );
    }
  }, [gender, voice, formApi]);

  const enumTranslations = useEnumTranslations();

  const { schema: voiceSchema } = useFieldsSchema(
    [
      {
        name: 'voiceConfig.gemini.voice',
        label: t`Voice`,
        type: 'select',
        options: Object.fromEntries(
          voices.map(value => [
            value,
            `${value} — ${GEMINI_VOICE_GENDERS[value] === Gender.Female ? t`Female` : t`Male`}, ${i18n._(voiceDescriptions[value])}`,
          ]),
        ),
        defaultValue:
          gender === Gender.Female ? GeminiVoices.Kore : GeminiVoices.Puck,
        showOptional: false,
      },
    ],
    [t, i18n, gender],
  );

  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'voiceConfig.gemini.startOfSpeechSensitivity',
        label: t`Start-of-speech sensitivity`,
        type: 'select',
        options: enumTranslations.voiceStartOfSpeechSensitivity,
        defaultValue: 'START_SENSITIVITY_LOW',
        tooltip: t`How readily the voicebot decides the caller has started speaking. High reacts faster but is more likely to be triggered by background noise; low waits for clearer speech.`,
        showOptional: false,
      },
      {
        name: 'voiceConfig.gemini.endOfSpeechSensitivity',
        label: t`End-of-speech sensitivity`,
        type: 'select',
        options: enumTranslations.voiceEndOfSpeechSensitivity,
        defaultValue: 'END_SENSITIVITY_LOW',
        tooltip: t`How readily the voicebot decides the caller has finished speaking. High ends the turn sooner after silence; low waits longer, allowing for pauses.`,
        showOptional: false,
      },
      {
        name: 'chatbotConfig.initialMessage',
        label: t`Initial message`,
        type: 'text',
        uiControl: 'textarea',
        defaultValue: t`Hi, I'm a voicebot. How can I help you today?`,
        showOptional: false,
      },
      {
        name: 'voiceConfig.keywords',
        label: t`Keywords (serparated by ",")`,
        placeholder: t`Kanbu, Utima, product names`,
        type: 'text',
        uiControl: 'textarea',
        tooltip: t`Comma-separated names and terms used as hints for caller transcription and the voicebot. Recognition is not guaranteed.`,
        showOptional: false,
        // @ts-expect-error - invalid types
        parser: (value: string) =>
          (value ?? '').split(',').map(keyword => keyword.trim()),
        initialize: (value: string[]) => (value ?? []).join(', '),
      },
      {
        name: 'voiceConfig.endpointing',
        label: t`Silence interval`,
        type: 'number',
        showOptional: false,
        defaultValue: 400,
        tooltip: t`How long the caller has to stay quiet before the voicebot decides they have finished speaking and starts answering. Phone lines need 400–600 ms: people pause mid-sentence to think, and a shorter window cuts them off and sends the voicebot half a question. Lower values (150–300 ms) make replies feel snappier and are only safe on the web tester, where the audio is clean. The 10 ms floor exists for experimentation — it is not a usable setting on a real call.`,
        uiProps: {
          addonAfter: 'ms',
        },
      },
      {
        name: 'voiceConfig.maxCallSeconds',
        label: t`Maximum call length`,
        type: 'number',
        showOptional: false,
        defaultValue: 600,
        tooltip: t`Hard limit on a single call. About a minute before it, the voicebot is told to start wrapping up; at the limit the call ends. A voice call costs more the longer it runs, so this is the ceiling that stops one stuck call from running up a bill.`,
        uiProps: {
          addonAfter: 's',
        },
      },
    ],
    [t, hasAccess, enumTranslations],
  );

  const typeFieldState = useFieldState('agentType');

  // These fields should be visible only if the agent type is Voice
  if (typeFieldState.value !== AgentType.Voice) {
    return null;
  }

  return (
    <Card title={t`Voice settings`}>
      <SchemaFields schema={voiceSchema} />
      <VoicePreviewButton />
      <SchemaFields schema={detailsSchema} />
    </Card>
  );
}

/**
 * Call-handling cards for voice chats: operator transfer and silence
 * handling. Split out from `VoiceConfigFields` so they can be placed in a
 * different form column (they render next to `TwilioConfigFields` rather
 * than under the main voice settings).
 */
export function VoiceCallHandlingFields() {
  const { hasAccess } = useAccess();
  const { t } = useLingui();

  const { schema: operatorTransferSchema } = useFieldsSchema(
    [
      {
        name: 'voiceConfig.operatorTransfer.enabled',
        label: t`Enable operator transfer`,
        tooltip: t`Let the voicebot hand the call off to a human operator when it can't help.`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: false,
        showOptional: false,
      },
      {
        // Only cold (blind) transfer is supported today — the schema
        // rejects any other value, so it is fixed rather than offered as
        // a choice. See the note rendered below the toggle.
        name: 'voiceConfig.operatorTransfer.mode',
        type: 'hidden',
        defaultValue: 'cold',
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            !!(
              formState as TypedFormState<{
                voiceConfig?: {
                  operatorTransfer?: { enabled?: boolean };
                };
              }>
            ).values?.voiceConfig?.operatorTransfer?.enabled,
          $fields: [
            {
              name: 'voiceConfig.operatorTransfer.fallbackPhone',
              label: t`Fallback phone number`,
              type: 'text',
              placeholder: '+14155552671',
              tooltip: t`Called when no active operator with a phone number is available, including outside business hours. Not retried after an unanswered operator call. Use international format, e.g. +420123456789.`,
              showOptional: true,
            },
            {
              name: 'voiceConfig.operatorTransfer.farewellMessage',
              label: t`Farewell message`,
              type: 'text',
              uiControl: 'textarea',
              tooltip: t`Spoken to the caller right before the call is transferred to the operator.`,
              defaultValue: t`One moment, I'm transferring you to a colleague who can help.`,
              showOptional: true,
            },
          ],
        },
      },
    ],
    [t],
  );

  const { schema: recordingSchema } = useFieldsSchema(
    [
      {
        name: 'voiceConfig.recording.enabled',
        label: t`Record phone calls`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: false,
        showOptional: false,
        tooltip: t`Record the full phone call, including operator transfers. The caller and the other side of the call use separate audio channels. Browser calls are not recorded.`,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            !!hasAccess([Role.SuperAdmin]) &&
            !!(
              formState as TypedFormState<{
                voiceConfig?: { recording?: { enabled?: boolean } };
              }>
            ).values?.voiceConfig?.recording?.enabled,
          $fields: [
            {
              name: 'voiceConfig.recording.retentionDays',
              label: t`Recording retention (days)`,
              type: 'number',
              defaultValue: 30,
              showOptional: false,
              tooltip: t`Applies to new recordings. Expired recordings are automatically deleted.`,
            },
          ],
        },
      },
    ],
    [t, hasAccess],
  );

  const typeFieldState = useFieldState('agentType');

  // These fields should be visible only if the agent type is Voice
  if (typeFieldState.value !== AgentType.Voice) {
    return null;
  }

  return (
    <>
      <Card title={t`Operator transfer`}>
        <SchemaFields schema={operatorTransferSchema} />
      </Card>

      <Card title={t`Phone call recording`}>
        <SchemaFields schema={recordingSchema} />
      </Card>

      <Card title={t`Silence settings`}>
        <Input
          name='voiceConfig.silenceTimeout'
          type='number'
          label={t`Silence timeout`}
          tooltip={t`When the user is silent for this amount of time, the voicebot will play a random message from the pool of silence messages.`}
          addonAfter='ms'
          showOptional={false}
          defaultValue={15000}
        />
        <ListInput
          label={t`Voice lines indicating AI is listening`}
          name='voiceConfig.silenceMessages'
          type='text'
          trimValues
          addItemLabel={t`Add new message`}
          minLines={1}
          defaultValue={[t`I'm listening`]}
        />
      </Card>
    </>
  );
}
